import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ethers } from 'ethers';
import Caver from 'caver-js';
import CURRENT_CHAIN from 'constants/chainInfo';

export interface WalletState {
  isLoading: boolean;
  isLoggedIn: boolean;
  info: { type: string; address: any; balance: any; network: any };
}

const initialState: WalletState = {
  isLoading: false,
  isLoggedIn: !!sessionStorage.getItem('address'),
  info: {
    type: null,
    address: JSON.parse(sessionStorage.getItem('address')),
    balance: JSON.parse(sessionStorage.getItem('balance')),
    network: null,
  },
};

const AddChain = async () => {
  const num = CURRENT_CHAIN.id;
  const params = {
    chainId: '0x' + num.toString(16), // A 0x-prefixed hexadecimal string
    chainName: CURRENT_CHAIN.name,
    nativeCurrency: {
      name: 'KLAY',
      symbol: 'KLAY', // 2-6 characters long
      decimals: 18,
    },
    rpcUrls: [CURRENT_CHAIN.rpcUrl],
    blockExplorerUrls: [CURRENT_CHAIN.blockExplorerUrl],
  };

  try {
    const result = await window.ethereum.request({
      method: 'wallet_addEthereumChain',
      params: [params],
    });
  } catch (addError) {
    console.log(addError);
  }
};

export const walletService = {
  connectKaikas: createAsyncThunk(
    'wallet/connectKaikas',
    async (_, { rejectWithValue }) => {
      try {
        const walletInstance = await window.klaytn.enable();
        const address = walletInstance[0];
        const balance = await window.caver.klay.getBalance(address);
        const network = await window.klaytn.networkVersion;

        if (network !== CURRENT_CHAIN.id) {
          alert(`네트워크를 ${CURRENT_CHAIN.name}으로 변경해 주세요.`);
          return rejectWithValue(
            `네트워크를 ${CURRENT_CHAIN.name}으로 변경해 주세요.`,
          );
        }

        sessionStorage.setItem(
          'balance',
          JSON.stringify(Caver.utils.fromPeb(balance, 'KLAY')),
        );
        sessionStorage.setItem('address', JSON.stringify(address));
        sessionStorage.setItem('network', JSON.stringify(network));

        return {
          type: 'kaikas',
          address,
          balance: Caver.utils.fromPeb(balance, 'KLAY'),
          network: window.klaytn.networkVersion,
        };
      } catch (error) {
        return rejectWithValue(error);
      }
    },
  ),
  disconnectKaikas: createAsyncThunk('wallet/disconnectKaikas', async () => {
    await window.caver.klay.accounts.wallet.clear();
    sessionStorage.removeItem('balance');
    sessionStorage.removeItem('address');
    sessionStorage.removeItem('network');
  }),
  connectMetamask: createAsyncThunk(
    'wallet/connectMetamask',
    async (_, { rejectWithValue }) => {
      try {
        const provider = new ethers.providers.Web3Provider(window.ethereum);
        await provider.send('eth_requestAccounts', []);
        const signer = provider.getSigner();

        const address = await signer.getAddress();
        const balance =
          parseInt((await signer.getBalance())._hex) / 1000000000000000000;
        const network = await signer.getChainId();

        if (network !== CURRENT_CHAIN.id) {
          alert(`네트워크를 ${CURRENT_CHAIN.name}으로 변경해 주세요.`);

          if (
            !window.confirm(
              `네트워크에 (${CURRENT_CHAIN.name})을 추가하시겠습니까?`,
            )
          ) {
            alert('네트워크 등록을 수행하지 않았습니다');
          } else {
            alert(
              '네트워크 등록이 완료되면, 네트워크를 변경하고 다시 시도해주세요.',
            );
            await AddChain();
          }

          return rejectWithValue(
            `네트워크를 ${CURRENT_CHAIN.name}으로 변경해 주세요.`,
          );
        }

        sessionStorage.setItem('address', JSON.stringify(address));
        sessionStorage.setItem('balance', JSON.stringify(balance));
        sessionStorage.setItem('network', JSON.stringify(network));

        return {
          type: 'metamask',
          address,
          balance: balance,
          network: network,
        };
      } catch (error) {
        return rejectWithValue(error);
      }
    },
  ),
  disconnectMetamask: createAsyncThunk(
    'wallet/disconnectMetamask',
    async () => {
      sessionStorage.removeItem('balance');
      sessionStorage.removeItem('address');
      sessionStorage.removeItem('network');
    },
  ),
};

const walletSlice = createSlice({
  name: 'wallet',
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(walletService.connectKaikas.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(walletService.connectKaikas.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isLoggedIn = true;
        state.info = action.payload;
      })
      .addCase(walletService.connectKaikas.rejected, (state, action) => {
        state.isLoading = false;
      })
      .addCase(walletService.disconnectKaikas.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(walletService.disconnectKaikas.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isLoggedIn = false;
        state.info = initialState.info;
      })
      .addCase(walletService.disconnectKaikas.rejected, (state, action) => {
        state.isLoading = false;
      })
      .addCase(walletService.connectMetamask.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(walletService.connectMetamask.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isLoggedIn = true;
        state.info = action.payload;
      })
      .addCase(walletService.connectMetamask.rejected, (state, action) => {
        state.isLoading = false;
      })
      .addCase(walletService.disconnectMetamask.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(walletService.disconnectMetamask.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isLoggedIn = false;
        state.info = initialState.info;
      })
      .addCase(walletService.disconnectMetamask.rejected, (state, action) => {
        state.isLoading = false;
      });
  },
});

export const walletActions = walletSlice.actions;
export default walletSlice;
